import { NIL_UUID } from '@/utils/uuid';
import type { UUID } from '@/utils/uuid';

import { useListConnectorFeaturesWithProviderOptions } from './use-list-connector-features-with-provider-options';

function resolveConfigureLabel(
  hasCredentials: boolean,
  hasSettings: boolean
): string {
  if (hasCredentials && hasSettings) return 'credentials and settings';
  if (hasCredentials) return 'credentials';
  return 'settings';
}

/**
 * Derives what configuration capabilities a connector has — whether it requires
 * credentials, shared settings, or both — by inspecting provider feature schema
 * and secret template data.
 */
export function useConnectorCapabilities(tenantId?: UUID, connectorId?: UUID) {
  const { data: enrichedFeatures } =
    useListConnectorFeaturesWithProviderOptions(tenantId, connectorId);

  const features = enrichedFeatures ?? [];

  const hasCredentials = features.some((f) =>
    (f.providerFeature?.credentials ?? []).some(
      (credential) =>
        credential.templateId && credential.templateId !== NIL_UUID
    )
  );

  const hasSettings = features.some((f) => {
    const schema = f.providerFeature?.optionsSchema;
    return (
      schema &&
      typeof schema !== 'boolean' &&
      schema.type === 'object' &&
      schema.properties &&
      Object.keys(schema.properties).length > 0
    );
  });

  const configureLabel = resolveConfigureLabel(hasCredentials, hasSettings);

  return { hasCredentials, hasSettings, configureLabel, enrichedFeatures };
}
